import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { Home, FileText, PlusCircle, Users, Truck, LayoutDashboard, LogOut, Bell, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import CaprocureLogo from "@/components/CaprocureLogo";

// Buyer / supplier navs stay role-native — Phase 3 only touches employee UI.
const BUYER_NAV = [
  { to: "/app", label: "Dashboard", icon: Home, exact: true },
  { to: "/app/rfqs", label: "My RFQs", icon: FileText },
  { to: "/app/rfqs/new", label: "Create RFQ", icon: PlusCircle },
  { to: "/app/transactions", label: "Transactions", icon: Truck },
];

const SUPPLIER_NAV = [
  { to: "/app", label: "Dashboard", icon: Home, exact: true },
  { to: "/app/rfqs", label: "Invited RFQs", icon: FileText },
  { to: "/app/transactions", label: "Awarded Contracts", icon: Truck },
];

// Employee nav is assembled from permissions returned by
// GET /api/auth/me/permissions. Each entry lists the permission that must be
// held for the link to render. Deny-by-default until permissions load.
const EMPLOYEE_NAV = [
  { to: "/app", label: "Overview", icon: Home, exact: true, perm: "dashboard.read" },
  { to: "/app/kanban", label: "Pipeline", icon: LayoutDashboard, perm: "rfq.list" },
  { to: "/app/rfqs", label: "RFQs", icon: FileText, perm: "rfq.list" },
  { to: "/app/suppliers", label: "Suppliers", icon: Building2, perm: "supplier.list" },
  { to: "/app/transactions", label: "Transactions", icon: Truck, perm: "tx.list" },
  { to: "/app/users", label: "Users", icon: Users, perm: "employee.list" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { can, loaded } = usePermissions();
  const nav = useNavigate();

  const items = (() => {
    if (user.role === "buyer") return BUYER_NAV;
    if (user.role === "supplier") return SUPPLIER_NAV;
    // Employees: deny-by-default while permissions load — render an empty
    // sidebar rather than briefly flashing every privileged link.
    if (!loaded) return [];
    return EMPLOYEE_NAV.filter((it) => can(it.perm));
  })();

  const roleLabel = user.is_owner ? "owner" : user.role.replace("_", " ");

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 bg-[#0A2540] text-white flex flex-col fixed inset-y-0 left-0">
        <div className="px-6 py-6 border-b border-white/10">
          <CaprocureLogo variant="dark" size="md" />
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1" data-testid="sidebar-nav">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.exact}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "bg-[#10385C] text-white" : "text-blue-100/80 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <it.icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-blue-100/70">Signed in as</div>
          <div className="text-sm font-medium truncate">{user.name}</div>
          <div className="text-[11px] text-blue-200/60 truncate">{user.company_name}</div>
        </div>
      </aside>
      <div className="flex-1 ml-64">
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200 h-16 flex items-center justify-between px-8">
          <div>
            <div className="eyebrow">Caprocure™</div>
            <div className="text-sm text-slate-500">Connecting Canadian Buyers with Trusted Suppliers</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 font-medium text-slate-600 uppercase tracking-wider" data-testid="role-badge">
              {roleLabel}
            </span>
            <button className="p-2 rounded-md hover:bg-slate-100 text-slate-600" data-testid="notifications-btn"><Bell className="w-4 h-4" /></button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => { await logout(); nav("/"); }}
              data-testid="logout-btn"
              className="gap-2"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </Button>
          </div>
        </header>
        <main className="p-8 max-w-[1400px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
